import type { DatasetDescriptor, TableConfig, TableLayout } from "@kaleidoscope-solutions/engine-wasm"
import type { RouteLocationRaw } from "vue-router"
import { defineStore } from "pinia"

export interface GridCellLike {
  row: number
  column: number
  cellKind?: unknown
  displayKind: unknown
  valueType?: string
  formattedValue?: string
  readOnly: boolean
  sortable: boolean
  expandCollapse: unknown
  icon?: string
  detailPageType?: string
  colspan: number
  rowspan: number
  hierarchyLevel: number
}

export interface GridLike {
  rowCount: number
  colCount: number
  [Symbol.iterator]: () => Iterator<Iterable<GridCellLike>>
}

export interface PanelsLike {
  Corner: GridCellLike[][]
  Columns: GridCellLike[][]
  Rows: GridCellLike[][]
  Body: GridCellLike[][]
}

/** Relationship/attribute info for a single dimension (type) on the view */
export interface DimensionTypeInfo {
  typeName: string
  attributes: DatasetDescriptor[]
}

export interface TableFormulaInfo {
  name: string
  formula: string
  formulaType: number
}

export interface TableIntegrationSourceInfo {
  target: number | string
  label: string
  lastRan?: string
}

export interface TableTransformationSample {
  sourceRows: { nativeKey: string; value: string }[]
  relationshipRows: { viewKey: string; nativeKey: string }[]
  transformedRows: { viewKey: string; nativeKey: string; value: string }[]
}

export interface TableTransformationDetail {
  transformedDatasetName: string
  viewKeyType: string
  nativeKeyType: string
  relationshipDatasetName: string
  relationshipKind?: string
  relationshipInverse?: string
  fixedDimensions?: { type: string; value: string }[]
  sample: TableTransformationSample
  blockFormula?: string
}

interface DetailsPanelBase {
  panelType: "table" | "column"
  viewName: string
  title: string
  sourceIsOverlayPage: boolean
}

export interface ColumnRelationshipDataset {
  name: string
  relationship?: string
  inverse?: string
  keyTypes: string[]
  valueTypes: string[]
}

export interface ColumnDatasetInfo extends ColumnRelationshipDataset {
  reasons: string[]
}

export interface ColumnListInfo {
  name: string
  keyTypes: string[]
  reasons: string[]
}

export interface ColumnFormulaDetail extends TableFormulaInfo {
  referencedFormulas: TableFormulaInfo[]
  referencedDatasets: ColumnDatasetInfo[]
  referencedLists: ColumnListInfo[]
}

export interface RelationshipPath {
  typeNames: string[]
  datasetNames: string[]
}

export interface ContentViewReference {
  name: string
  tableType?: string
  rowTypeNames: string[]
  columnTypeNames: string[]
  matchedBy: string[]
}

export interface ContentPageReference {
  name: string
  parentName?: string
  sectionTitle?: string
  sectionId?: string
  widgetTitle?: string
  widgetId?: string
  viewName?: string
  matchedBy: string[]
}

interface ColumnDetailsBase extends DetailsPanelBase {
  panelType: "column"
  columnName: string
  objectIdentifier?: string
  entityType?: string
  columnIndex: number
  headerRow: number
  panelName: string
  displayKind: string
  cellKind?: string
  valueType?: string
  sortDirection: string
  sortable: boolean
  readOnly: boolean
  colspan: number
  rowspan: number
  isDataset: boolean
  isList: boolean
  isCalculated: boolean
  cellCount: number
  uniqueValueCount: number
  listItemCountsByListName?: Record<string, number>
  layoutMatches: string[]
  formulaDetail?: ColumnFormulaDetail
  relatedDatasets: ColumnDatasetInfo[]
  relatedLists: ColumnListInfo[]
  relationDatasets: ColumnRelationshipDataset[]
  relationshipTypes: string[]
  relationshipPaths: RelationshipPath[]
  relatedViews: ContentViewReference[]
  relatedPages: ContentPageReference[]
}

export type ColumnDetailsData = ColumnDetailsBase & (
  | {
    /** Engine-resolved canonical view for this element (list or block). */
    previewViewName: string
    /** Which kind the user drilled into when this column inspect panel was opened. */
    previewTargetKind: "block" | "list"
  } |
  {
    previewViewName?: undefined
    previewTargetKind?: undefined
  }
)

export interface TableDetailsData extends DetailsPanelBase {
  panelType: "table"
  isHomeTable?: boolean
  viewKeyTypes?: string[]
  numericDatasetNames?: string[]
  importTargetDatasetNames?: string[]
  integrationSources?: TableIntegrationSourceInfo[]
  numericDatasetDescriptors?: DatasetDescriptor[]
  dimensionTypeInfo?: DimensionTypeInfo[]
  formulasOnView?: TableFormulaInfo[]
  transformationDetails?: TableTransformationDetail[]
  relationshipPaths?: RelationshipPath[]
  relatedViews?: ContentViewReference[]
  relatedPages?: ContentPageReference[]
  summaryAllowed?: boolean
  config: TableConfig
  layout: TableLayout
  rowCount: number
  columnCount: number
  rowHeaderCount: number
  columnHeaderCount: number
  grid?: GridLike
  panels?: PanelsLike
  columnHasDatasets?: boolean
  rowHasDatasets?: boolean
}

export type DetailsPanelData = TableDetailsData | ColumnDetailsData

export type TableDetailsOrientation = "vertical" | "horizontal"
export type InspectChipSection = "block" | "row" | "column" | "relationship"
export type InspectHomeTargetKind = "block" | "list"

export interface InspectHomeTarget {
  kind: InspectHomeTargetKind
  route?: RouteLocationRaw | null
  viewName?: string
}

export interface InspectChipRequest {
  requestId: number
  sourceViewName: string
  targetViewName?: string
  targetTableAnchor?: string
  targetKind?: InspectHomeTargetKind
  homeTargetKind?: InspectHomeTargetKind
  homeTargetRoute?: RouteLocationRaw | null
  homeTargetViewName?: string
  section: InspectChipSection
  label: string
  index?: number
}

export interface RegisteredInspectTable {
  viewName: string
  tableAnchor: string
  rowHeaderLabels: string[]
  columnHeaderLabels: string[]
  allHeaderLabels: string[]
}

export interface ResolveStrictDrillTargetInput {
  label: string
  section: InspectChipSection | "any"
  preferredViews?: Array<string | undefined>
}

export interface ResolvedStrictDrillTarget {
  viewName: string
  tableAnchor: string
}

interface State {
  isOpen: boolean
  isLoading: boolean
  details?: DetailsPanelData
  inspectBackStack: DetailsPanelData[]
  inspectHomeTarget?: InspectHomeTarget
  pendingChipInspect?: InspectChipRequest
  nextInspectChipRequestId: number
  panelWidth: number
  panelHeight: number
  orientation: TableDetailsOrientation
  inspectTableRegistry: Record<string, RegisteredInspectTable>
  hoveredDimensionOverlay?: {
    viewName: string
    section: "block" | "row" | "column" | "relationship"
    label: string
    overlayClass: string
    index?: number
    role?: "group" | "header" | "context" | "type"
  }
}

function sameInspectTarget(left: DetailsPanelData, right: DetailsPanelData): boolean {
  if (left.panelType !== right.panelType) {
    return false
  }

  if (left.viewName !== right.viewName) {
    return false
  }

  if (left.panelType === "table" && right.panelType === "table") {
    return true
  }

  if (left.panelType === "column" && right.panelType === "column") {
    return left.columnIndex === right.columnIndex &&
      left.headerRow === right.headerRow &&
      left.panelName === right.panelName &&
      left.objectIdentifier === right.objectIdentifier
  }

  return false
}

function normaliseIdentifier(value: string): string {
  const trimmed = String(value ?? "").trim()
  if (trimmed.length < 2) {
    return trimmed.toLowerCase()
  }

  const first = trimmed[0]
  const last = trimmed[trimmed.length - 1]
  if ((first === "'" && last === "'") || (first === "\"" && last === "\"")) {
    return trimmed.slice(1, -1).trim().toLowerCase()
  }
  return trimmed.toLowerCase()
}

export function normaliseLooseIdentifier(value: string): string {
  return normaliseIdentifier(value).replace(/[^a-z0-9]/gi, "")
}

export function normaliseLooseSingularIdentifier(value: string): string {
  const loose = normaliseLooseIdentifier(value)
  if (loose.endsWith("s") && loose.length > 2) {
    return loose.slice(0, -1)
  }
  return loose
}

export function sameIdentifierLoose(left: string, right: string): boolean {
  const exactLeft = normaliseLooseIdentifier(left)
  const exactRight = normaliseLooseIdentifier(right)
  if (exactLeft && exactLeft === exactRight) {
    return true
  }

  const singularLeft = normaliseLooseSingularIdentifier(left)
  const singularRight = normaliseLooseSingularIdentifier(right)
  return singularLeft.length > 0 && singularLeft === singularRight
}

function sameIdentifierStrict(left: string, right: string): boolean {
  if (normaliseIdentifier(left) === normaliseIdentifier(right)) {
    return true
  }
  const leftLoose = normaliseLooseSingularIdentifier(left)
  const rightLoose = normaliseLooseSingularIdentifier(right)
  return Boolean(leftLoose) && leftLoose === rightLoose
}

function uniqueLabels(labels: string[]): string[] {
  const output: string[] = []
  const seen = new Set<string>()

  for (const label of labels) {
    const value = String(label ?? "").trim()
    if (!value) {
      continue
    }
    const key = normaliseIdentifier(value)
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    output.push(value)
  }

  return output
}

function labelParts(value: string): string[] {
  return uniqueLabels(
    String(value ?? "")
      .split(/::|->|[>,|]/g)
      .map((part) => part.trim())
      .filter(Boolean)
  )
}

function labelsForSection(
  table: RegisteredInspectTable,
  section: InspectChipSection | "any"
): string[] {
  if (section === "any") {
    return table.allHeaderLabels
  }
  if (section === "row") {
    return table.rowHeaderLabels
  }
  if (section === "column") {
    return table.columnHeaderLabels
  }
  return table.allHeaderLabels
}

function preferredViewRank(
  viewName: string,
  preferredViews: Array<string | undefined>
): number {
  const normalisedView = normaliseIdentifier(viewName)
  if (!normalisedView) {
    return Number.POSITIVE_INFINITY
  }

  for (let index = 0; index < preferredViews.length; index += 1) {
    const candidate = normaliseIdentifier(preferredViews[index] ?? "")
    if (!candidate) {
      continue
    }
    if (candidate === normalisedView) {
      return index
    }
  }

  return Number.POSITIVE_INFINITY
}

function sameViewNameStrict(left: string, right: string): boolean {
  return normaliseIdentifier(left) === normaliseIdentifier(right)
}

function sameViewNameLoose(left: string, right: string): boolean {
  if (sameViewNameStrict(left, right)) {
    return true
  }

  const normalizedLeft = normaliseLooseIdentifier(left)
  const normalizedRight = normaliseLooseIdentifier(right)
  if (!normalizedLeft || !normalizedRight) {
    return false
  }

  return normalizedLeft.includes(normalizedRight) || normalizedRight.includes(normalizedLeft)
}

export const useTableInspectStore = defineStore("inspect", {
  state: (): State => ({
    isOpen: false,
    isLoading: false,
    details: undefined,
    inspectBackStack: [],
    inspectHomeTarget: undefined,
    pendingChipInspect: undefined,
    nextInspectChipRequestId: 1,
    panelWidth: 500,
    panelHeight: 320,
    orientation: "vertical",
    inspectTableRegistry: {},
    hoveredDimensionOverlay: undefined,
  }),
  getters: {
    hasOpenDetails: (state) => state.isOpen && Boolean(state.details),
    canGoBack: (state) => state.inspectBackStack.length > 0,
    isOpenForSource: (state) => (sourceIsOverlayPage: boolean) =>
      state.isOpen &&
      Boolean(state.details) &&
      state.details?.sourceIsOverlayPage === sourceIsOverlayPage,
    isDetailsOpenForView: (state) => (
      viewName: string,
      sourceIsOverlayPage?: boolean
    ) => {
      if (!state.isOpen || !state.details || state.details.viewName !== viewName) {
        return false
      }
      if (sourceIsOverlayPage === undefined) {
        return true
      }
      return state.details.sourceIsOverlayPage === sourceIsOverlayPage
    },
  },
  actions: {
    pushPreviousInspectTargetIfNeeded(currentDetails: DetailsPanelData) {
      const previous = this.inspectBackStack[this.inspectBackStack.length - 1]
      if (!previous || !sameInspectTarget(previous, currentDetails)) {
        this.inspectBackStack = [...this.inspectBackStack, currentDetails]
      }
    },
    resetTransientInspectState() {
      this.pendingChipInspect = undefined
      this.hoveredDimensionOverlay = undefined
    },
    setActiveDetails(details: DetailsPanelData) {
      this.isOpen = true
      this.isLoading = false
      this.details = details
      this.resetTransientInspectState()
    },
    open(details: DetailsPanelData) {
      this.setActiveDetails(details)
      this.inspectBackStack = []
      this.inspectHomeTarget = undefined
    },
    openFromInspect(details: DetailsPanelData) {
      if (this.details && sameInspectTarget(this.details, details)) {
        this.setActiveDetails(details)
        return
      }

      if (this.details) {
        this.pushPreviousInspectTargetIfNeeded(this.details)
      }

      this.setActiveDetails(details)
    },
    goBack() {
      const previous = this.inspectBackStack[this.inspectBackStack.length - 1]
      if (!previous) {
        return
      }

      this.inspectBackStack = this.inspectBackStack.slice(0, -1)
      this.isLoading = false
      this.inspectHomeTarget = undefined
      this.setActiveDetails(previous)
    },
    requestChipInspect(payload: Omit<InspectChipRequest, "requestId">) {
      const requestId = this.nextInspectChipRequestId++
      this.isLoading = true
      this.pendingChipInspect = {
        ...payload,
        requestId,
      }
      return requestId
    },
    claimChipInspectRequest(requestId: number) {
      if (!this.pendingChipInspect || this.pendingChipInspect.requestId !== requestId) {
        return false
      }

      this.pendingChipInspect = undefined
      return true
    },
    clearChipInspectRequest(requestId?: number) {
      if (!this.pendingChipInspect) {
        return
      }

      if (requestId !== undefined && this.pendingChipInspect.requestId !== requestId) {
        return
      }

      this.pendingChipInspect = undefined
      this.isLoading = false
    },
    registerInspectTable(table: RegisteredInspectTable) {
      if (!table.tableAnchor) {
        return
      }

      this.inspectTableRegistry = {
        ...this.inspectTableRegistry,
        [table.tableAnchor]: {
          ...table,
          rowHeaderLabels: uniqueLabels(table.rowHeaderLabels),
          columnHeaderLabels: uniqueLabels(table.columnHeaderLabels),
          allHeaderLabels: uniqueLabels(table.allHeaderLabels),
        },
      }
    },
    unregisterInspectTable(tableAnchor: string) {
      if (!tableAnchor || !this.inspectTableRegistry[tableAnchor]) {
        return
      }

      const nextRegistry = { ...this.inspectTableRegistry }
      delete nextRegistry[tableAnchor]
      this.inspectTableRegistry = nextRegistry
    },
    resolveRegisteredInspectTableByViewName(viewName: string): ResolvedStrictDrillTarget | undefined {
      const requestedViewName = String(viewName ?? "").trim()
      if (!requestedViewName) {
        return undefined
      }

      const strictMatches = Object.values(this.inspectTableRegistry)
        .filter((table) => sameViewNameStrict(table.viewName, requestedViewName))
        .sort((left, right) => left.tableAnchor.localeCompare(right.tableAnchor))

      if (strictMatches.length === 1) {
        const [match] = strictMatches
        if (!match) {
          return undefined
        }

        return {
          viewName: match.viewName,
          tableAnchor: match.tableAnchor,
        }
      }

      if (strictMatches.length > 1) {
        return undefined
      }

      const looseMatches = Object.values(this.inspectTableRegistry)
        .filter((table) => sameViewNameLoose(table.viewName, requestedViewName))
        .sort((left, right) => left.tableAnchor.localeCompare(right.tableAnchor))

      if (looseMatches.length !== 1) {
        return undefined
      }

      const [match] = looseMatches
      if (!match) {
        return undefined
      }

      return {
        viewName: match.viewName,
        tableAnchor: match.tableAnchor,
      }
    },
    resolveStrictDrillTarget(input: ResolveStrictDrillTargetInput): ResolvedStrictDrillTarget | undefined {
      const requestedLabel = String(input.label ?? "").trim()
      if (!requestedLabel) {
        return undefined
      }

      const candidates = uniqueLabels([requestedLabel, ...labelParts(requestedLabel)])
      if (candidates.length === 0) {
        return undefined
      }

      const preferredViews = input.preferredViews ?? []
      const matches = Object.values(this.inspectTableRegistry)
        .filter((table) => {
          const tableLabels = labelsForSection(table, input.section)
          if (tableLabels.length === 0) {
            return false
          }

          return tableLabels.some((tableLabel) =>
            candidates.some((candidate) => sameIdentifierStrict(candidate, tableLabel))
          )
        })
        .map((table) => ({
          table,
          rank: preferredViewRank(table.viewName, preferredViews),
        }))
        .sort((left, right) => {
          if (left.rank !== right.rank) {
            return left.rank - right.rank
          }
          const viewOrder = left.table.viewName.localeCompare(right.table.viewName)
          if (viewOrder !== 0) {
            return viewOrder
          }
          return left.table.tableAnchor.localeCompare(right.table.tableAnchor)
        })

      const [best] = matches
      if (!best) {
        return undefined
      }

      const competing = matches.filter((match) => match.rank === best.rank)
      if (competing.length !== 1) {
        return undefined
      }

      return {
        viewName: best.table.viewName,
        tableAnchor: best.table.tableAnchor,
      }
    },
    close() {
      this.isOpen = false
      this.isLoading = false
      this.inspectBackStack = []
      this.inspectHomeTarget = undefined
      this.resetTransientInspectState()
    },
    update(details: Partial<TableDetailsData>) {
      if (!this.details || this.details.panelType !== "table") {
        return
      }
      this.details = {
        ...this.details,
        ...details,
      }
    },
    setHoveredDimensionOverlay(overlay: State["hoveredDimensionOverlay"]) {
      this.hoveredDimensionOverlay = overlay
    },
    clearHoveredDimensionOverlay() {
      this.hoveredDimensionOverlay = undefined
    },
    setPanelWidth(width: number) {
      this.panelWidth = width
    },
    setPanelHeight(height: number) {
      this.panelHeight = height
    },
    toggleOrientation() {
      this.orientation = this.orientation === "vertical" ? "horizontal" : "vertical"
    },
    clear() {
      this.isOpen = false
      this.isLoading = false
      this.details = undefined
      this.inspectBackStack = []
      this.inspectHomeTarget = undefined
      this.resetTransientInspectState()
    },
    setInspectHomeTarget(target?: InspectHomeTarget) {
      this.inspectHomeTarget = target
    },
    setLoading(loading: boolean) {
      this.isLoading = loading
    },
  },
})
